/**
 * Per-UID storage of the user's most-recently-selected organization.
 *
 * Mirrors the mobile pattern in `apps/mobile/src/hooks/useOrganization.ts`
 * (commit 5e4c… in the 1.3.1 round). Keying by Firebase UID is critical: if
 * two people share a browser profile (think: a family iPad, a clerk's
 * shared workstation, a stake leader who occasionally logs in as a demo
 * account during App Review), we don't want one user's last-picked org to
 * auto-route the next user into the wrong tenant. The previous web flow
 * used a single global `selectedOrgId` which leaked across accounts.
 *
 * All three functions are no-ops when `uid` is null/undefined or when
 * `localStorage` itself is unavailable (private mode, storage disabled),
 * so callers don't need to wrap them in try/catch.
 */

const PREFIX = 'selectedOrgId'

function key(uid: string | null | undefined): string | null {
  if (!uid) return null
  return `${PREFIX}:${uid}`
}

export function getSavedOrgId(uid: string | null | undefined): string | null {
  const k = key(uid)
  if (!k) return null
  try {
    return localStorage.getItem(k)
  } catch {
    return null
  }
}

export function setSavedOrgId(uid: string | null | undefined, orgId: string): void {
  const k = key(uid)
  if (!k || !orgId) return
  try {
    localStorage.setItem(k, orgId)
  } catch {
    // localStorage can throw in Safari private mode or when quota is
    // exceeded — silently ignore; the app still works, just won't
    // remember the selection across reloads.
  }
}

export function clearSavedOrgId(uid: string | null | undefined): void {
  const k = key(uid)
  if (!k) return
  try {
    localStorage.removeItem(k)
  } catch {
    // ignore — see setSavedOrgId
  }
}
